<template>
  <div class="segment-feature-assertion-component" :class="{ 'has-error': error }">
    <VdFormField prop="featureId" :error="(error && error.featureId) || ''">
      <VersatileSelect
        :class="$style.selectMetric"
        :model-value="form.featureId"
        :disabled="readOnly"
        placeholder="Select a metric"
        option-label="label"
        option-value="value"
        :options="featureChoices.sort((a, b) => a.label.localeCompare(b.label))"
        has-search
        @update:model-value="onFeatureChanged"
      >
        <template #header>
          <div :class="$style.filterWrapper">
            <span
              :class="{ [$style.selected]: displayedFeaturesSource === 'system' }"
              @click="onFilterMetrics('system')"
            >
              System metrics
            </span>
            <span
              :class="{ [$style.selected]: displayedFeaturesSource === 'custom' }"
              @click="onFilterMetrics('custom')"
            >
              Custom metrics
            </span>
          </div>
        </template>
      </VersatileSelect>
    </VdFormField>

    <VdFormField
      v-if="!isBooleanFeature"
      :key="form.featureId ? form.featureId : generateRandomId()"
      prop="operator"
      :error="(error && error.operator) || ''"
    >
      <VdSelect
        :disabled="!form.featureId || readOnly"
        :model-value="form.operator"
        placeholder="Select an operator"
        :choices="getOperatorChoices"
        @update:model-value="onOperatorChanged"
      />
    </VdFormField>

    <VersatileFormField
      :key="`${form.featureId}-${form.operator}`"
      prop="testValue"
      :type="selectedFeatureType"
      :disabled="!form.featureId || (!isBooleanFeature && !form.operator) || readOnly"
      :model-value="form.testValue"
      :placeholder="isBooleanFeature ? 'Select an operator' : 'Enter a value'"
      :error="(error && error.testValue) || ''"
      :choices="((selectedFeature && selectedFeature.availableValues) || []).map((o) => ({ label: o, value: o }))"
      update-on-blur-only
      :hide-controls="readOnly"
      @update:model-value="onTestValueChanged"
    />

    <vd-dropdown v-if="!readOnly" class="assertion-dropdown">
      <vd-dropdown-item @click="onAdd(SegmentConditionType.And)">AND</vd-dropdown-item>
      <vd-dropdown-item @click="onAdd(SegmentConditionType.Or)">OR</vd-dropdown-item>
      <vd-dropdown-item v-if="!disableDeleteButton" dangerous @click="onDelete">Delete</vd-dropdown-item>
    </vd-dropdown>
  </div>
</template>

<script setup lang="ts">
import { VdFormField, VdSelect } from "@voodoo.io/voodoo-ui";
import { PropType, computed, reactive, ref, watch } from "vue";

import VersatileFormField from "@/components/structure/VersatileFormField/VersatileFormField.vue";
import {
  FeatureOperator,
  FeatureType,
  SegmentConditionType,
  SegmentFormPageFeatureFragment,
  SegmentFormPageSegmentConditionRecursiveFragment,
  featureOperatorLabels,
} from "@/graphql";
import { generateRandomId } from "@/utils";

import { ConditionError } from "../utils";

interface FeatureAssertionError {
  featureId: string;
  operator: string;
  testValue: string;
}

const props = defineProps({
  features: {
    type: Array as PropType<SegmentFormPageFeatureFragment[]>,
    required: true,
  },

  disableDeleteButton: Boolean,

  /**
   * The test value of the assertion
   */
  modelValue: {
    type: Object as PropType<Omit<SegmentFormPageSegmentConditionRecursiveFragment, "type">>,
    default: () => ({}),
  },

  /**
   * if true, the form field are disabled
   */
  readOnly: Boolean,

  /**
   * The error object to pass to the form fields
   */
  error: {
    type: Object as PropType<FeatureAssertionError | ConditionError>,
    default: () => null,
  },
});

const emit = defineEmits(["update:modelValue", "delete", "add"]);

const form = reactive<Omit<SegmentFormPageSegmentConditionRecursiveFragment, "type">>({
  featureId: "",
  operator: null,
  testValue: null,
});

const displayedFeatures = ref(props.features);
const displayedFeaturesSource = ref<string | null>(null);

watch(
  () => props.modelValue,
  (watchedValue) => {
    if (!watchedValue) {
      return;
    }
    form.featureId = watchedValue.featureId ?? "";
    form.operator = watchedValue.operator ?? null;
    form.testValue = watchedValue.testValue;
  },
  { immediate: true }
);

const onFeatureChanged = (featureId: string) => {
  form.featureId = featureId;
  form.operator = null;
  form.testValue = null;
};

const onOperatorChanged = (operator: FeatureOperator) => {
  form.operator = operator;
  form.testValue = null;
};

const onTestValueChanged = (testValue: unknown) => {
  form.testValue = testValue;

  if (form.featureId != null && form.operator != null) {
    emit("update:modelValue", form);
  }
};

const onDelete = () => {
  emit("delete");
};

const onAdd = (conditionType: SegmentConditionType) => {
  emit("add", conditionType);
};

/**
 * Utils
 */
const selectedFeature = computed(() => {
  if (!form.featureId) {
    return null;
  }
  const featuresById = Object.fromEntries(props.features.map((feat) => [feat.id, feat]));
  return featuresById[form.featureId];
});

const isBooleanFeature = computed(() => selectedFeature.value && selectedFeature.value.type === FeatureType.Boolean);

watch(isBooleanFeature, (watchedIsBooleanFeature) => {
  if (watchedIsBooleanFeature) {
    form.operator = FeatureOperator.Equals;
  } else {
    form.operator = null;
  }
});

const selectedFeatureType = computed(() => {
  if (!selectedFeature.value) {
    return "text";
  }

  if (!form.operator) {
    return isBooleanFeature.value ? selectedFeature.value.formFieldType : "text";
  }

  if (selectedFeature.value.type === FeatureType.Select) {
    if ([FeatureOperator.Equals, FeatureOperator.NotEquals].includes(form.operator)) {
      return "select";
    }

    if ([FeatureOperator.In, FeatureOperator.NotIn].includes(form.operator)) {
      return "multiselect";
    }

    if ([FeatureOperator.Contains, FeatureOperator.NotContains].includes(form.operator)) {
      return "text";
    }
  }

  if (selectedFeature.value.type === FeatureType.String) {
    return [FeatureOperator.In, FeatureOperator.NotIn].includes(form.operator) ? "string[]" : "text";
  }

  return selectedFeature.value.formFieldType;
});

/**
 * Feature select choices
 */
const featureChoices = computed(() =>
  (displayedFeatures.value || []).map(({ name, id, type, source }) => {
    const label = name === id ? name : `${name} (${id} • ${type})`;
    return { label, value: id, source };
  })
);

/**
 * Operator select choices
 */
const getOperatorChoices = computed(() => {
  if (!selectedFeature.value) {
    return [];
  }
  return selectedFeature.value.operators.map((o) => ({ label: featureOperatorLabels[o], value: o }));
});

/**
 * Filter metrics by source
 */
const onFilterMetrics = (source: string) => {
  if (source) {
    if (displayedFeaturesSource.value === source) {
      displayedFeatures.value = props.features;
      displayedFeaturesSource.value = null;
      return;
    }

    if (source === "system") {
      displayedFeatures.value = props.features.filter((f) => f.source === "system");
    } else {
      displayedFeatures.value = props.features.filter((f) => f.source !== "system");
    }

    displayedFeaturesSource.value = source;
  }
};
</script>

<style lang="scss">
.segment-feature-assertion-component {
  display: grid;
  grid-template-columns: repeat(3, 1fr) auto;
  align-items: center;
  margin-bottom: size("M");
  column-gap: 10px;

  .p-select-label {
    padding: 0 0.75rem;
  }

  &.has-error {
    margin-bottom: size("ML");
  }

  .vd-input,
  .vd-select {
    width: 100%;
    justify-content: center;
  }

  .vd-form-field {
    &:not(:nth-of-type(3)) {
      margin-right: size("M");
    }
  }

  .assertion-dropdown {
    justify-self: end;
  }
}
</style>

<style module lang="scss">
.selectMetric {
  max-width: 400px;
}

.filterWrapper {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  padding-bottom: 2px;

  span {
    cursor: pointer;
    color: var(--vd-color-blue-600);
    font-size: 1rem;
    border: 1px solid var(--vd-color-blue-600);
    padding: 0.5rem 1rem;
    border-radius: 16px;

    &.selected {
      background-color: var(--vd-color-blue-600);
      color: white;
    }
  }
}
</style>
